import { useEffect, useRef, useState } from "react";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { useSnackbar } from "../../components/Snackbar";
import Spinner from "../../components/Spinner";
import Select from "../../components/Select";
import { useQueryClient } from "@tanstack/react-query";
import useClientNameOptsQuery from "../../hooks/data/queries/useClientNameOptsQuery";
import useUpdateAccountHolderCompanyName from "../../hooks/data/mutations/useUpdateAccountHolderCompanyName";
import { s } from "msw/lib/glossary-c690f512";

// TODO: Remove this and create call to get company name options
const companyNames = [] as const;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accountHolderCode: string;
};

function EditClientNameModal({
  isOpen,
  onClose,
  accountHolderCode,
}: Props): JSX.Element {
  const [clientName, setClientName] = useState<string>("");
  const typeRef = useRef<HTMLSelectElement | null>(null);

  const {
    data: clientNameOpts,
    isLoading: clientNameOptsIsLoading,
    error: clientNameOptsDataError,
  } = useClientNameOptsQuery(accountHolderCode);

  const { mutate: updateCompanyName, isLoading: isUpdatingCompanyName } =
    useUpdateAccountHolderCompanyName();

  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  // TODO: Change this to play off the loading of company names
  const isLoadingClientNames = false;

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => typeRef.current?.focus(), 0);
    }
  }, [isOpen]);

  function handleUpdateCompanyName() {
    updateCompanyName(
      {
        accountHolderCode: accountHolderCode,
        companyName: clientName,
      },
      {
        onSuccess() {
          queryClient.invalidateQueries(["accountConfig"]);
          openSnackbar("Client name updated.", {
            type: "success",
          });
          setClientName("");
          onClose();
        },
        onError(e: string) {
          openSnackbar(e, {
            type: "error",
          });
        },
      }
    );
  }

  const disableSaveButton =
    isLoadingClientNames || isUpdatingCompanyName || clientName === "";

  return (
    <Modal
      isOpen={isOpen}
      title="Edit Client Name"
      panelClasses="!w-80"
      titleClasses="text-left"
      onClose={() => {
        onClose();
      }}
    >
      <div className="mt-10 flex flex-col gap-y-12">
        <Select
          id="clientName"
          label="Select Client Name"
          options={
            clientNameOpts?.map((x) => ({
              value: x,
              label: x,
            })) || []
          }
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          loading={isLoadingClientNames}
          errorMessage={clientNameOptsDataError ?? undefined}
          ref={typeRef}
        />
        <Button
          onClick={() => handleUpdateCompanyName()}
          disabled={disableSaveButton}
          icon={isUpdatingCompanyName && <Spinner className="text-gray-400" />}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}

export default EditClientNameModal;
